import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, RotateCw, ExternalLink, X, Globe, Lock } from "lucide-react";

// Navegador webview incorporado: overlay a pantalla completa con una toolbar
// (cerrar, atrás, adelante, recargar, barra de URL, abrir en navegador externo)
// y un <iframe> que carga el sitio del rastreador/mapa sin salir de la app.
//
// Montado en la raíz (App.jsx). Escucha el evento `bhgc-open-inapp-browser`
// despachado por openInAppBrowser() (usado por InAppLink). Mantiene su propia
// pila de URLs (atrás/adelante) porque el iframe es cross-origin y no se puede
// leer su historial interno.
//
// Nota: algunos sitios (DHL, FedEx, Google Maps) envían X-Frame-Options: DENY
// y se niegan a incrustarse; el iframe mostrará el error del navegador. Por eso
// la toolbar siempre ofrece "abrir en navegador externo" como escape.
export default function InAppBrowser() {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [stack, setStack] = useState([]);
  const [idx, setIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [slow, setSlow] = useState(false);
  const iframeRef = useRef(null);
  const slowTimer = useRef(null);

  useEffect(() => {
    const handler = (ev) => {
      ev.preventDefault(); // señala a openInAppBrowser que fue atendido
      const u = ev.detail?.url;
      if (!u) return;
      setUrl(u);
      setStack([u]);
      setIdx(0);
      setLoading(true);
      setSlow(false);
      setOpen(true);
    };
    window.addEventListener("bhgc-open-inapp-browser", handler);
    return () => window.removeEventListener("bhgc-open-inapp-browser", handler);
  }, []);

  // Aviso de carga lenta / sitio que bloquea incrustación.
  useEffect(() => {
    if (!open) return;
    clearTimeout(slowTimer.current);
    setSlow(false);
    slowTimer.current = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(slowTimer.current);
  }, [open, url]);

  // Cerrar con Escape (desktop) / teclado de accesibilidad.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    setUrl("");
    setStack([]);
    setIdx(-1);
    setLoading(false);
    setSlow(false);
  };

  const navigate = (i) => {
    if (i < 0 || i >= stack.length) return;
    setIdx(i);
    setUrl(stack[i]);
    setLoading(true);
    setSlow(false);
  };

  const reload = () => {
    setLoading(true);
    setSlow(false);
    const f = iframeRef.current;
    if (f) {
      const s = f.src;
      f.src = "about:blank";
      requestAnimationFrame(() => { if (f) f.src = s; });
    }
  };

  const openExternal = () => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  let host = "";
  try { host = new URL(url).hostname.replace(/^www\./, ""); } catch { host = url; }

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-background flex flex-col">
      {/* Toolbar / headerbar */}
      <div className="flex items-center gap-1 px-2 sm:px-3 py-2 border-b border-ink-border bg-ink-surface safe-top">
        <button type="button" onClick={close} className="touch-icon p-2 text-ink-text hover:text-cyan shrink-0" title="Cerrar" aria-label="Cerrar navegador">
          <X className="w-5 h-5" />
        </button>
        <span className="hidden sm:flex items-center gap-1.5 px-2 text-[0.7rem] uppercase tracking-wider font-display font-bold text-cyan shrink-0">
          <Globe className="w-3.5 h-3.5" /> Navegador
        </span>
        <div className="w-px h-6 bg-ink-border mx-0.5 shrink-0" />
        <button type="button" onClick={() => navigate(idx - 1)} disabled={idx <= 0} className="touch-icon p-2 text-ink-text hover:text-cyan disabled:opacity-30 disabled:hover:text-ink-text shrink-0" title="Atrás" aria-label="Atrás">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => navigate(idx + 1)} disabled={idx >= stack.length - 1} className="touch-icon p-2 text-ink-text hover:text-cyan disabled:opacity-30 disabled:hover:text-ink-text shrink-0" title="Adelante" aria-label="Adelante">
          <ArrowRight className="w-5 h-5" />
        </button>
        <button type="button" onClick={reload} className="touch-icon p-2 text-ink-text hover:text-cyan shrink-0" title="Recargar" aria-label="Recargar">
          <RotateCw className="w-5 h-5" />
        </button>
        {/* Barra de URL */}
        <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 surface-inset text-xs text-ink-muted truncate">
          <Lock className="w-3.5 h-3.5 shrink-0 text-success" />
          <span className="truncate font-mono">{host}</span>
        </div>
        <button type="button" onClick={openExternal} className="touch-icon p-2 text-ink-text hover:text-cyan shrink-0" title="Abrir en navegador externo" aria-label="Abrir en navegador externo">
          <ExternalLink className="w-5 h-5" />
        </button>
      </div>

      {/* Contenido */}
      <div className="relative flex-1 min-h-0 bg-white">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/85 z-10">
            <div className="flex flex-col items-center gap-3 text-ink-muted">
              <RotateCw className="w-6 h-6 animate-spin text-cyan" />
              <span className="text-xs font-medium">Cargando…</span>
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={url}
          title="Navegador BHGC"
          className="w-full h-full border-0 bg-white"
          onLoad={() => { setLoading(false); setSlow(false); }}
          referrerPolicy="no-referrer-when-downgrade"
          allow="geolocation"
        />
        {/* Aviso de sitio que bloquea incrustación */}
        {slow && (
          <div className="absolute left-1/2 bottom-4 -translate-x-1/2 z-20 max-w-[92%] sm:max-w-md surface border border-warning/50 px-4 py-3 flex items-center gap-3 shadow-lg">
            <Lock className="w-4 h-4 text-warning shrink-0" />
            <p className="text-xs text-ink-text leading-snug flex-1">
              Si la página no carga, el sitio no permite incrustarse.
            </p>
            <button type="button" onClick={openExternal} className="shrink-0 flex items-center gap-1.5 text-xs font-bold text-cyan hover:underline whitespace-nowrap">
              <ExternalLink className="w-3.5 h-3.5" /> Abrir fuera
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}